import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getGuestToken } from "@/lib/guest";
import ZButton from "@/components/ZButton";
import { ZInput } from "@/components/ZInput";
import { Check, X, ArrowUp, ArrowDown, AlertTriangle } from "lucide-react";

export default function VotePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [vote, setVote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Per-type state
  const [single, setSingle] = useState(null);
  const [multi, setMulti] = useState([]);
  const [binary, setBinary] = useState(null);
  const [approvals, setApprovals] = useState({}); // {opt_id: true|false}
  const [ranked, setRanked] = useState([]); // ordered list of opt_ids
  const [voterEmail, setVoterEmail] = useState("");

  const load = () => {
    const token = !user ? getGuestToken() : null;
    const qs = token ? `?voter_token=${encodeURIComponent(token)}` : "";
    api.get(`/votes/${id}${qs}`)
      .then((r) => {
        setVote(r.data);
        if (r.data.vote_type === "ranked") setRanked(r.data.options.map((o) => o.id));
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [id, user]);

  const submit = async () => {
    setErr("");
    const payload = {};
    if (!user) payload.voter_token = getGuestToken();
    if (vote.restricted) {
      if (!voterEmail.trim() && !user) {
        return setErr("Veuillez saisir votre e-mail (liste électorale).");
      }
      if (!user) payload.voter_email = voterEmail.trim();
    }
    const vt = vote.vote_type;
    if (vt === "single") {
      if (!single) return setErr("Sélectionnez une option.");
      payload.option_ids = [single];
    } else if (vt === "multi") {
      if (multi.length === 0) return setErr("Sélectionnez au moins une option.");
      payload.option_ids = multi;
    } else if (vt === "binary") {
      if (!binary) return setErr("Choisissez Oui, Non ou Abstention.");
      payload.binary_choice = binary;
    } else if (vt === "approval") {
      if (Object.keys(approvals).length === 0) return setErr("Approuvez ou rejetez chaque option.");
      payload.approvals = approvals;
    } else if (vt === "ranked") {
      if (ranked.length === 0) return setErr("Classez les options.");
      payload.ranked_option_ids = ranked;
    }

    setSubmitting(true);
    try {
      await api.post(`/votes/${id}/cast`, payload);
      load();
    } catch (e) {
      setErr(errMsg(e));
    } finally { setSubmitting(false); }
  };

  const move = (idx, dir) => {
    const arr = [...ranked];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setRanked(arr);
  };

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  if (!vote) return <div className="z-container">Vote introuvable.</div>;

  const vt = vote.vote_type;
  const showResults = vote.user_voted || vote.options.some((o) => o.count !== undefined && o.count !== null && (o.count > 0 || o.borda_score > 0 || o.approve_count > 0 || o.reject_count > 0));
  const totalCount = vote.options.reduce((s, o) => s + (o.count || 0), 0) || 1;
  const bordaTotal = vote.options.reduce((s, o) => s + (o.borda_score || 0), 0) || 1;
  const optById = (id_) => vote.options.find((o) => o.id === id_);

  const TYPE_LABELS = {
    single: "Choix unique",
    multi: "Choix multiples",
    binary: "Vote binaire",
    approval: "Approbation",
    ranked: "Vote par rang",
  };

  return (
    <div className="z-container" data-testid="vote-page" style={{ maxWidth: 600 }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
        <span className="z-badge z-badge-teal">{TYPE_LABELS[vt]}</span>
        {vote.restricted && <span className="z-badge">🔒 Vote restreint</span>}
        {vote.quorum_pct > 0 && <span className="z-badge">Quorum {vote.quorum_pct}%</span>}
      </div>
      <h1 className="z-page-title" data-testid="vote-title">{vote.title}</h1>
      {vote.description && <p className="z-page-sub">{vote.description}</p>}

      {vote.user_voted && (
        <div style={{ background: "rgba(67,122,34,0.10)", color: "var(--z-success)", padding: 12, borderRadius: 10, marginBottom: 14, fontWeight: 600 }} data-testid="vote-already">
          ✓ Votre vote a été enregistré.
        </div>
      )}

      {showResults && vote.restricted && vote.participation_pct !== null && (
        <div style={{ background: vote.quorum_reached ? "rgba(67,122,34,0.10)" : "rgba(218,113,1,0.10)", color: vote.quorum_reached ? "var(--z-success)" : "var(--z-orange)", padding: 12, borderRadius: 10, marginBottom: 14, fontSize: 14, display: "flex", gap: 8, alignItems: "center" }} data-testid="quorum-status">
          {vote.quorum_reached ? <Check size={16} /> : <AlertTriangle size={16} />}
          Participation : {vote.participation_pct}% / {vote.quorum_pct}% requis — quorum {vote.quorum_reached ? "atteint" : "non atteint"}
        </div>
      )}

      <div className="z-card" style={{ padding: 18 }}>
        {showResults ? (
          // ===== RESULTS =====
          <div style={{ display: "grid", gap: 12 }}>
            {vt === "ranked" ? (
              [...vote.options].sort((a, b) => (b.borda_score || 0) - (a.borda_score || 0)).map((o, idx) => {
                const pct = bordaTotal > 0 ? Math.round(((o.borda_score || 0) / bordaTotal) * 100) : 0;
                return (
                  <div key={o.id} data-testid={`vote-result-${o.id}`}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontWeight: 600 }}>{idx + 1}. {o.label}</span>
                      <span style={{ color: "var(--z-muted)", fontSize: 13 }}>{(o.borda_score || 0).toFixed(1)} pts ({pct}%)</span>
                    </div>
                    <div className="z-progress"><div className="z-progress-fill" style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })
            ) : vt === "approval" ? (
              vote.options.map((o) => {
                const a = o.approve_count || 0, r = o.reject_count || 0;
                const sum = (a + r) || 1;
                return (
                  <div key={o.id} data-testid={`vote-result-${o.id}`}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{o.label}</div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <div style={{ flex: a, background: "var(--z-success)", height: 18, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, minWidth: a > 0 ? 30 : 0 }}>{a > 0 && `${a} ✓`}</div>
                      <div style={{ flex: r, background: "var(--z-error)", height: 18, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, minWidth: r > 0 ? 30 : 0 }}>{r > 0 && `${r} ✗`}</div>
                    </div>
                  </div>
                );
              })
            ) : (
              vote.options.map((o) => {
                const pct = totalCount > 0 ? Math.round(((o.count || 0) / totalCount) * 100) : 0;
                return (
                  <div key={o.id} data-testid={`vote-result-${o.id}`}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontWeight: 600 }}>{o.label}</span>
                      <span style={{ color: "var(--z-muted)", fontSize: 13 }}>{o.count || 0} ({pct}%)</span>
                    </div>
                    <div className="z-progress"><div className="z-progress-fill" style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          // ===== CAST FORM =====
          <>
            {vote.restricted && !user && (
              <ZInput type="email" label="Votre e-mail (liste électorale)" value={voterEmail} onChange={(e) => setVoterEmail(e.target.value)} required placeholder="vous@exemple.com" data-testid="voter-email" />
            )}

            {vt === "single" && (
              <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
                {vote.options.map((o) => (
                  <button key={o.id} type="button" onClick={() => setSingle(o.id)}
                    data-testid={`vote-option-${o.id}`}
                    className="z-chip" style={{ padding: "14px 16px", justifyContent: "flex-start", ...(single === o.id ? { background: "var(--z-teal)", color: "white", borderColor: "var(--z-teal)" } : {}) }}
                  >{o.label}</button>
                ))}
              </div>
            )}

            {vt === "multi" && (
              <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
                {vote.options.map((o) => {
                  const active = multi.includes(o.id);
                  return (
                    <button key={o.id} type="button"
                      onClick={() => setMulti(active ? multi.filter((x) => x !== o.id) : [...multi, o.id])}
                      data-testid={`vote-option-${o.id}`}
                      className="z-chip" style={{ padding: "14px 16px", justifyContent: "flex-start", ...(active ? { background: "var(--z-teal)", color: "white", borderColor: "var(--z-teal)" } : {}) }}
                    >{active ? "✓ " : ""}{o.label}</button>
                  );
                })}
                <p className="z-help" style={{ textAlign: "center" }}>Plusieurs choix possibles</p>
              </div>
            )}

            {vt === "binary" && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 16 }}>
                {[
                  { id: "yes", label: "✓ Oui", color: "var(--z-success)" },
                  { id: "no", label: "✗ Non", color: "var(--z-error)" },
                  { id: "abstain", label: "○ Abstention", color: "var(--z-muted)" },
                ].map((b) => (
                  <button key={b.id} type="button" onClick={() => setBinary(b.id)}
                    data-testid={`vote-binary-${b.id}`}
                    className="z-chip"
                    style={{ padding: "20px 14px", flexDirection: "column", fontSize: 16, fontWeight: 700, ...(binary === b.id ? { background: b.color, color: "white", borderColor: b.color } : {}) }}
                  >{b.label}</button>
                ))}
              </div>
            )}

            {vt === "approval" && (
              <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
                {vote.options.map((o) => (
                  <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", border: "1.5px solid var(--z-border)", borderRadius: 12 }} data-testid={`vote-approval-${o.id}`}>
                    <span style={{ flex: 1, fontWeight: 500 }}>{o.label}</span>
                    <button type="button" onClick={() => setApprovals({ ...approvals, [o.id]: true })}
                      className="z-btn z-btn-sm" style={{ background: approvals[o.id] === true ? "var(--z-success)" : "transparent", color: approvals[o.id] === true ? "white" : "var(--z-success)", border: "1.5px solid var(--z-success)" }}
                      data-testid={`approve-${o.id}`}><Check size={14} /></button>
                    <button type="button" onClick={() => setApprovals({ ...approvals, [o.id]: false })}
                      className="z-btn z-btn-sm" style={{ background: approvals[o.id] === false ? "var(--z-error)" : "transparent", color: approvals[o.id] === false ? "white" : "var(--z-error)", border: "1.5px solid var(--z-error)" }}
                      data-testid={`reject-${o.id}`}><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}

            {vt === "ranked" && (
              <div style={{ display: "grid", gap: 6, marginBottom: 16 }}>
                <p className="z-help" style={{ marginBottom: 4 }}>Classez du plus préféré (en haut) au moins préféré (en bas) :</p>
                {ranked.map((oid, idx) => {
                  const o = optById(oid);
                  return (
                    <div key={oid} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", border: "1.5px solid var(--z-border)", borderRadius: 10, background: "white" }} data-testid={`ranked-item-${oid}`}>
                      <span style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--z-teal)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13 }}>{idx + 1}</span>
                      <span style={{ flex: 1, fontWeight: 500 }}>{o?.label}</span>
                      <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="z-btn z-btn-ghost z-btn-sm" data-testid={`rank-up-${oid}`}><ArrowUp size={14} /></button>
                      <button type="button" onClick={() => move(idx, 1)} disabled={idx === ranked.length - 1} className="z-btn z-btn-ghost z-btn-sm" data-testid={`rank-down-${oid}`}><ArrowDown size={14} /></button>
                    </div>
                  );
                })}
              </div>
            )}

            {err && <p className="z-error-text" data-testid="vote-error">{err}</p>}
            <ZButton onClick={submit} block loading={submitting} data-testid="vote-submit-btn">Voter</ZButton>
          </>
        )}
        <div style={{ marginTop: 14, fontSize: 13, color: "var(--z-muted)", textAlign: "center" }}>
          {vote.total_votes} participation(s){vote.restricted && vote.electoral_count ? ` / ${vote.electoral_count} électeurs` : ""}
        </div>
      </div>
    </div>
  );
}
